import React from 'react';
import styles from './CircuitTraces.module.css';

/**
 * Trazas de circuito impreso con pulsos de corriente, de fondo de la sección Developer.
 *
 * ## Por qué SVG con `stroke-dashoffset` y no otra cosa
 *
 * 🔑 Es la técnica **más segura** de este proyecto para animar movimiento. La regla del
 * proyecto es que las animaciones que desplazan elementos han provocado barras de
 * scroll parásitas más de una vez, y `stroke-dashoffset` no desplaza nada: cambia
 * **cómo se pinta** una línea que ya está donde está. No hay reflujo, no hay repintado
 * del layout, y el navegador lo compone en la GPU.
 *
 * Y de paso es la que mejor se ve: un guion corto recorriendo una traza con esquinas se
 * lee exactamente como corriente pasando por una pista de PCB, que es el efecto pedido.
 *
 * ## Cómo está armado
 *
 * Dos capas por traza:
 * 1. La **pista** — línea continua muy tenue. Es el cobre: siempre está ahí.
 * 2. El **pulso** — la misma ruta con `stroke-dasharray` de un guion corto y un hueco
 *    largo, animando `stroke-dashoffset`. Es la corriente.
 *
 * Cada traza lleva su propio retardo y duración para que los pulsos no vayan en fila
 * india, que es lo que delataría que es una animación y no un circuito.
 *
 * `preserveAspectRatio="none"` a propósito: el bloque cambia de ancho al colapsar el
 * sidebar y las trazas deben estirarse con él en lugar de recortarse.
 */

/** Rutas con esquinas rectas y chaflanes, como las de una placa real. */
const TRACES: Array<{ d: string; dur: number; delay: number }> = [
  { d: 'M0 14 H26 L34 22 H72 L80 14 H120', dur: 3.4, delay: 0 },
  { d: 'M0 34 H18 L26 26 H58 L66 34 H120', dur: 4.1, delay: 0.9 },
  { d: 'M0 54 H44 L52 46 H96 L104 54 H120', dur: 3.8, delay: 1.7 },
  { d: 'M0 74 H30 L38 66 H84 L92 74 H120', dur: 4.6, delay: 0.4 },
];

/** Nodos de soldadura donde las trazas se cruzan o cambian de dirección. */
const PADS: Array<{ cx: number; cy: number; delay: number }> = [
  { cx: 26, cy: 14, delay: 0 },
  { cx: 72, cy: 22, delay: 0.7 },
  { cx: 26, cy: 26, delay: 1.3 },
  { cx: 96, cy: 46, delay: 0.5 },
  { cx: 38, cy: 66, delay: 1.9 },
  { cx: 84, cy: 74, delay: 1.1 },
];

const CircuitTraces: React.FC = () => (
  <svg
    className={styles.svg}
    viewBox="0 0 120 88"
    preserveAspectRatio="none"
    aria-hidden="true"
    focusable="false"
  >
    {TRACES.map((t, i) => (
      <g key={i}>
        {/* Pista de cobre: siempre visible, muy tenue. */}
        <path className={styles.track} d={t.d} />
        {/* Pulso de corriente. La duración y el retardo van en línea porque son
            distintos por traza: con todos iguales los pulsos irían en fila india. */}
        <path
          className={styles.pulse}
          d={t.d}
          style={{ animationDuration: `${t.dur}s`, animationDelay: `${t.delay}s` }}
        />
      </g>
    ))}

    {PADS.map((p, i) => (
      <circle
        key={i}
        className={styles.pad}
        cx={p.cx}
        cy={p.cy}
        r={1.6}
        style={{ animationDelay: `${p.delay}s` }}
      />
    ))}
  </svg>
);

export default CircuitTraces;
